import React, { useRef, useState } from 'react';
import useClickOutside from '../../hooks/useClickOutside';

type Props = {
  label?: string;
  options: string[];
  selected: string[];
  setSelected: (s: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxVisibleChips?: number;
  setReview?: (v: boolean) => void;
  review?: boolean;
};

export default function MultiSelectDropdown({
  label,
  options,
  selected,
  setSelected,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  maxVisibleChips = 4,
  review,
  setReview,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(containerRef, () => setOpen(false));

  const query = search.trim().toLowerCase();
  const selectedMatching = selected.filter((s) => s.toLowerCase().includes(query));
  const remaining = options.filter((o) => !selected.includes(o));
  const remainingMatching = remaining.filter((o) => o.toLowerCase().includes(query));

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      setSelected(selected.filter((s) => s !== option));
    } else {
      setSelected([...selected, option]);
    }
  };

  const removeChip = (option: string) => {
    setSelected(selected.filter((s) => s !== option));
  };

  return (
    <div className="c-tab-content__input-wrap ">
      {label && <label>{label}</label>}

      <div className={`o-multi-select-two ${review ? '-reviewmode' : ''}`} tabIndex={0} ref={containerRef}>
        <div className={`o-multi-select-two__head ${open ? '-active' : ''}`}>
          <div className="o-input-wrap">
            <i className="icon-search"></i>
          </div>
          <i
            className={`o-multi-select-two__arrow icon-caret ${open ? '-active' : ''}`}
            style={{ zIndex: 1 }}
            onClick={() => setOpen((v) => !v)}
            tabIndex={0}
          />

          {selected.length === 0 ? (
            <ul className="o-multi-select-two__items -list">
              <li className="o-multi-select-two__placeholder">{placeholder}</li>
            </ul>
          ) : (
            <ul className="o-multi-select-two__items -list">
              {selected.slice(0, maxVisibleChips).map((s) => (
                <li key={s} className="o-multi-select-two__item -active" tabIndex={0}>
                  <span>{s}</span>
                  <button
                    role="button"
                    className="c-tab-content__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChip(s);
                    }}
                  >
                    <i className="icon-close" />
                  </button>
                </li>
              ))}

              {selected.length > maxVisibleChips && (
                <li className="o-multi-select-two__item -chip" tabIndex={0}>
                  +{selected.length - maxVisibleChips}
                  <div className="c-tab-content__tooltip" style={{ position: 'absolute' }}>
                    <ul className="o-multi-select-two__items">
                      {selected.slice(maxVisibleChips).map((s) => (
                        <li key={s} className="o-multi-select-two__item -active" tabIndex={0}>
                          {s}
                          <button
                            role="button"
                            className="c-tab-content__close"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeChip(s);
                            }}
                          >
                            <i className="icon-close" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>

        <div className={`o-multi-select-two__dropdown -top-position ${open ? '-active' : ''}`} style={{ zIndex: 1 }}>
          <div className="c-dropdown-item" role="option">
            <div className="o-input-wrap">
              <i className="icon-search" />
              <input
                type="text"
                className="o-input"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="-scroll Scrollbar">
            {selectedMatching.length > 0 && (
              <>
                {selectedMatching.map((s) => (
                  <a
                    key={s}
                    href="#!"
                    role="option"
                    className="c-dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleOption(s);
                    }}
                  >
                    <div className="o-checkbox">
                      <input type="checkbox" checked readOnly />
                      <span>{s}</span>
                    </div>
                  </a>
                ))}
              </>
            )}

            <div className="c-dropdown-item__title">Other</div>
            <div className="c-dropdown-item__option">
              {remainingMatching.length > 0 ? (
                remainingMatching.map((o) => (
                  <a
                    key={o}
                    href="#!"
                    role="option"
                    className="c-dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleOption(o);
                    }}
                  >
                    <div className="o-checkbox">
                      <input type="checkbox" checked={false} readOnly />
                      <span>{o}</span>
                    </div>
                  </a>
                ))
              ) : (
                <div className="c-dropdown-item -empty">No results</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
